import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Breadcrumb from '../../../components/ui/Breadcrumb';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Card, CardContent } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import ComparisonModal from '../components/ComparisonModal';
import { useToast } from '../../../components/ui/Toast';
import { regularizationService, getErrorMessage } from '../../../services/apiService';

/** Where the regularization screens live now; this page is reached from a send's "Review N submitted". */
const HUB_PATH = '/campaigning-tool/regularization';

/** A link past its date is expired whether or not anything has marked the row so yet (as the hub reads it). */
const displayStatus = (request) =>
  request.status === 'SENT' && request.expires_at && new Date(request.expires_at) <= new Date() ? 'EXPIRED' : request.status;

/** Answers waiting for review first, then links still out, then the closed ones. */
const ORDER = { SUBMITTED: 0, SENT: 1, EXPIRED: 2, VERIFIED: 3 };

const BatchDetails = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = user?.role?.toUpperCase() === 'ADMIN';

  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchBatchDetails();
  }, [batchId, isAdmin, navigate]);

  const fetchBatchDetails = async () => {
    try {
      setLoading(true);
      const response = await regularizationService.getBatchDetails(Number(batchId));
      if (response.success) {
        setBatch(response.data);
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to fetch batch details');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (requestId) => {
    try {
      const response = await regularizationService.verifyRequest(requestId);
      if (response.success) {
        toast.success('Vendor changes verified and applied successfully');
        fetchBatchDetails();
        setShowComparison(false);
        setSelectedRequest(null);
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Failed to verify request');
    }
  };

  const handleReviewChanges = (request) => {
    setSelectedRequest(request);
    setShowComparison(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      SENT: { variant: 'info', label: 'Link sent' },
      SUBMITTED: { variant: 'warning', label: 'Submitted' },
      VERIFIED: { variant: 'success', label: 'Verified' },
      EXPIRED: { variant: 'destructive', label: 'Expired' },
    };
    const config = statusConfig[status] || statusConfig.SENT;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (!isAdmin) {
    return null;
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-gray-600">Batch not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusCounts = batch.requests.reduce((acc, req) => {
    const status = displayStatus(req);
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
  const requests = [...batch.requests].sort((a, b) => (ORDER[displayStatus(a)] ?? 9) - (ORDER[displayStatus(b)] ?? 9));

  const stats = [
    { label: 'Total vendors', value: batch.requests.length, className: 'text-gray-900' },
    { label: 'Waiting for the vendor', value: statusCounts.SENT || 0, className: 'text-gray-900' },
    { label: 'Submitted — to review', value: statusCounts.SUBMITTED || 0, className: 'text-amber-600' },
    { label: 'Verified', value: statusCounts.VERIFIED || 0, className: 'text-emerald-600' },
    { label: 'Expired', value: statusCounts.EXPIRED || 0, className: 'text-gray-500' },
  ];

  return (
    <div className="p-6">
      <Breadcrumb
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Vendor Regularization', path: HUB_PATH },
          { label: `Batch #${batchId}`, path: `/vendor-regularization/batch/${batchId}` },
        ]}
      />

      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Batch #{batchId}</h1>
            <p className="text-gray-600 mt-1">
              Sent on {new Date(batch.sent_at).toLocaleDateString()}
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate(HUB_PATH)}>
            <Icon name="ArrowLeft" size={18} className="mr-2" />
            Back to regularization
          </Button>
        </div>
      </div>

      {/* Batch Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
        {stats.map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-gray-500 mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.className}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Vendor List */}
      <div className="space-y-4">
        {requests.map((request) => {
          const status = displayStatus(request);
          return (
            <Card key={request.id}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {request.vendor.company_name}
                      </h3>
                      {getStatusBadge(status)}
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="mr-4">Vendor Code: {request.vendor.vendor_code || 'No code yet'}</span>
                      <span>Email: {request.vendor.email || 'none'}</span>
                    </div>
                    {request.submitted_at && (
                      <p className="text-xs text-gray-500 mt-2">
                        Submitted on {new Date(request.submitted_at).toLocaleDateString()}
                      </p>
                    )}
                    {request.verified_at && (
                      <p className="text-xs text-green-600 mt-2">
                        Verified on {new Date(request.verified_at).toLocaleDateString()}
                        {request.verifier && ` by ${request.verifier.full_name}`}
                      </p>
                    )}
                  </div>
                  <div>
                    {status === 'SUBMITTED' && (
                      <Button onClick={() => handleReviewChanges(request)}>
                        Review Changes
                      </Button>
                    )}
                    {status === 'VERIFIED' && (
                      <span className="text-sm text-emerald-600 font-medium">Verified</span>
                    )}
                    {status === 'EXPIRED' && (
                      <span className="text-sm text-red-600 font-medium">Expired</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {showComparison && selectedRequest && (
        <ComparisonModal
          request={selectedRequest}
          onClose={() => {
            setShowComparison(false);
            setSelectedRequest(null);
          }}
          onVerify={() => handleVerify(selectedRequest.id)}
        />
      )}
    </div>
  );
};

export default BatchDetails;
